import React from "react";
import { StyleSheet } from "react-native";
import BulbIcon from "../../../../assets/BulbIcon";
import ShieldIcon from "../../../../assets/ShieldIcon";
import EarIcon from "../../../../assets/EarIcon";
import HeartHandshakeIcon from "../../../../assets/HeartHandshakeIcon";
import PlantIcon from "../../../../assets/PlantIcon";
import LockIcon from "../../../../assets/LockIcon";

export const PREDEFINED_MEETING_NAMES = [
  "Custom / Make your own...",
  "Animals/pets",
  "Anything",
  "Music",
  "TV/Movies",
  "Self improvement",
  "Short term goals",
  "Wins/successes",
  "Travel and adventures",
  "Knexo first timer",
  "Future plans",
  "Healthy habits",
];

// figma: 1796x1165  | default screens: 1440x1024
const CONTENT_MAX_WIDTH = 1796;
const CONTENT_MIN_HEIGHT = 1165;
const GRID_WIDTH = 1293;
const FORM_WIDTH = 762;
const FORM_INNER_WIDTH = 682;
const STEP_ROW_WIDTH = 758;
const MOBILE_CONTENT_WIDTH = 375;
export const MOBILE_STEP_ROW_WIDTH = 317;
export const BETA_TIME_SLOT_NOTICE =
  "While we are in the beta phase, there will be limited time slots available. We will continue to add time slots as we continue to add users. Thank you for your patience.";

export const sharedCareRules = [
  {
    title: "Respect boundaries",
    description:
      "Everyone brings their own comfort level - and that always comes first.",
    icon: <ShieldIcon width={44} height={44} />,
    expandedContent: [
      "Ask for consent before changing topics or going deeper into sensitive subjects.",
      "Pay attention to emotional signals and respond with curiosity, not defensiveness.",
      "It's okay to pause, slow down, or redirect a conversation at any time.",
    ],
  },
  {
    title: "Listen with presence",
    description: "Let others finish. Acknowledge before responding.",
    icon: <EarIcon width={44} height={44} />,
    expandedContent: [
      "Show that you're engaged - through attentive posture, gentle expressions, and presence.",
      "Allow others to finish their thoughts before responding.",
      "Acknowledge what the other person shared before adding your own perspective.",
    ],
  },
  {
    title: "Communicate with care",
    description: "Tone and body language shape how conversations feel.",
    icon: <HeartHandshakeIcon width={44} height={44} />,
    expandedContent: [
      "Use a calm, friendly, and respectful tone.",
      "Keep your body language open and welcoming.",
      "Avoid gestures, expressions, or reactions that could feel dismissive, aggressive, or judgmental.",
    ],
  },
  {
    title: "Kindness over performance",
    description: "This is a practice space. Awkward is okay.",
    icon: <PlantIcon width={44} height={44} />,
    expandedContent: [
      "Speak genuinely and authentically; avoid sarcasm or language that could alienate others.",
      "Use affirming words and encourage when it feels appropriate",
      "Feeling awkward is okay. Learning together is the point. Be kind to yourself and to others.",
    ],
  },
  {
    title: "Trust & safety",
    description: "No recording. No pressure. You can leave or report anytime.",
    icon: <LockIcon width={44} height={44} />,
    expandedContent: [
      "Never record, photograph, or share interactions without clear permission.",
      "Using the platform to promote personal businesses or services without authorization is not allowed and may result in restricted access.",
    ],
  },
  {
    title: "Feedback with intention",
    description: "Offer feedback only when invited.",
    icon: <BulbIcon width={44} height={44} />,
    expandedContent: [
      "Offer feedback only when invited - and always with care and respect.",
      "Receive feedback with openness; it's an opportunity to grow, not a judgment.",
      "Avoid unsolicited advice or critique.",
    ],
  },
];

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#f6f7f9",
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    alignSelf: "center",
  },
  contentDesktop: {
    width: "100%",
    maxWidth: CONTENT_MAX_WIDTH,
    minHeight: CONTENT_MIN_HEIGHT,
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 40,
    paddingTop: 24,
    paddingBottom: 24,
    alignSelf: "center",
  },
  contentMobile: {
    width: "100%",
    maxWidth: MOBILE_CONTENT_WIDTH,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24,
  },
  mainContent: {
    width: "100%",
    alignItems: "center",
  },
  pageTitle: {
    fontSize: 36,
    fontWeight: "700",
    color: "#2b2b2f",
    textAlign: "center",
    letterSpacing: -1,
    marginTop: 24,
    marginBottom: 24,
  },
  pageTitleMobile: {
    fontSize: 26,
    lineHeight: 28,
    letterSpacing: -0.4,
    marginTop: 8,
    marginBottom: 16,
  },
  stepIndicatorWrapper: {
    alignItems: "center",
  },
  stepIndicator: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 40,
    gap: 67,
    width: "100%",
    maxWidth: STEP_ROW_WIDTH,
    alignSelf: "center",
  },
  stepIndicatorMobile: {
    width: "100%",
    gap: 67,
    marginBottom: 24,
  },
  stepContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  stepCircle: {
    width: 61,
    height: 61,
    borderRadius: 30.5,
    borderWidth: 1,
    borderColor: "#d63c8a",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  stepCircleActive: {
    backgroundColor: "#d63c8a",
    borderColor: "#d63c8a",
  },
  stepNumber: {
    fontSize: 20,
    fontWeight: "700",
    color: "#d63c8a",
  },
  stepNumberActive: {
    color: "#ffffff",
  },
  stepLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: "#2b2b2f",
    textAlign: "center",
    textTransform: "uppercase",
    opacity: 0.4,
  },
  stepLabelActive: {
    color: "#d63c8a",
    opacity: 1,
  },
  stepLabelCompleted: {
    color: "#2b2b2f",
    opacity: 1,
  },
  stepLabelMobile: {
    fontSize: 14,
    fontWeight: "700",
    color: "#d63c8a",
    textTransform: "uppercase",
    marginBottom: 24,
    textAlign: "center",
  },
  stepContent: {
    paddingTop: 8,
    paddingBottom: 16,
    alignItems: "center",
  },
  stepContentMobile: {
    width: "100%",
    paddingTop: 0,
    paddingBottom: 16,
  },
  gridContainer: {
    width: "100%",
    maxWidth: GRID_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    paddingHorizontal: 8,
    gap: 24,
  },
  gridContainerMobile: {
    maxWidth: "100%",
    flexDirection: "column",
    alignItems: "center",
    paddingHorizontal: 0,
    gap: 38,
  },
  topicsGridContainer: {
    width: "100%",
    maxWidth: GRID_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    paddingHorizontal: 8,
    gap: 24,
  },
  topicsGridContainerMobile: {
    maxWidth: "100%",
    flexDirection: "column",
    alignItems: "center",
    paddingHorizontal: 0,
    gap: 24,
  },
  formCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    borderColor: "#d8dbe1",
    width: FORM_WIDTH,
    minHeight: 709,
  },
  formCardMobile: {
    width: "100%",
    maxWidth: 343,
    paddingVertical: 32,
    paddingHorizontal: 16,
    minHeight: undefined,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#2b2b2f",
    marginBottom: 10,
    textTransform: "uppercase",
  },
  input: {
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ddd",
    paddingHorizontal: 12,
    backgroundColor: "#fff",
    fontSize: 16,
    color: "#333",
    width: FORM_INNER_WIDTH,
  },
  inputMobile: {
    width: "100%",
    maxWidth: 311,
    paddingHorizontal: 12,
    alignSelf: "center",
  },
  inputFocused: {
    borderColor: "#3a6fb1",
  },
  inputError: {
    borderColor: "#e53e3e",
    borderWidth: 1,
  },
  errorText: {
    color: "#e53e3e",
    fontSize: 12,
    marginTop: 6,
    fontWeight: "500",
  },
  helperText: {
    marginTop: 8,
    color: "#8f8f8f",
    fontSize: 12,
  },
  helperTextMobile: {
    marginTop: 12,
  },
  betaNotice: {
    marginBottom: 16,
    color: "#2b2b2f",
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.72,
  },
  betaNoticeMobile: {
    fontSize: 13,
    lineHeight: 19,
  },
  footerSection: {
    width: "100%",
    alignItems: "center",
    paddingBottom: 16,
  },
  footerSectionMobile: {
    paddingBottom: 32,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 24,
    paddingVertical: 12,
    backgroundColor: "transparent",
  },
  footerMobile: {
    width: "100%",
    maxWidth: 343,
    paddingVertical: 0,
  },
  cancelButton: {
    height: 62,
    minWidth: 259,
    paddingHorizontal: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#d8dbe1",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
  cancelButtonMobile: {
    flex: 1,
    minWidth: 130,
    maxWidth: 159.5,
    paddingHorizontal: 4,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#2b2b2f",
    textTransform: "uppercase",
  },
  continueButton: {
    height: 62,
    minWidth: 259,
    paddingHorizontal: 20,
    borderRadius: 16,
    backgroundColor: "#3a6fb1",
    alignItems: "center",
    justifyContent: "center",
  },
  continueButtonMobile: {
    flex: 1,
    minWidth: 130,
    maxWidth: 159.5,
    paddingHorizontal: 4,
  },
  continueButtonDisabled: {
    backgroundColor: "#ccc",
    opacity: 0.6,
  },
  continueButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#fff",
    textTransform: "uppercase",
  },
  bottomFooter: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingVertical: 8,
    backgroundColor: "transparent",
    marginTop: 64,
  },
  footerSpacer: {
    flex: 1,
  },
  copyright: {
    fontSize: 11,
    color: "#2b2b2f",
  },
  feedbackWrapper: {
    flex: 1,
    alignItems: "flex-end",
  },
  feedbackButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 24,
    borderRadius: 100,
    backgroundColor: "#7eadeb",
  },
  feedbackText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#f6f7f9",
  },
});
